import {
  CachedReportURL,
  CachedReportURLType,
  MetaDataQueryMode,
  ViewSet,
} from '@sas-dvr/ltjs-bird';
import { ltjsProperty, ltjsDestructor } from '../ltjs-decorators';
import { FileType, Report, ReportAssetURLType, ReportManifest } from '@sas-dvr/ltjs-transport';
import { ReportSession } from '@sas-dvr/ltjs-vav';
import { SASServer, getDepot } from './SASServer';
import { TransportVoucher } from './TransportVoucher';
import { embeddedBundle } from '@sas-dvr/internal-va-react-core/locale/embeddedI18N';
import { ResourceManager } from '@sas-dvr/ltjs-commons';
import { ReplaySubject, Observable, BehaviorSubject } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';
import { ltjsModule } from '@sas-dvr/ltjs-commons/ltjsEnvironment';
import { IReport, ReportReadyState } from './internal/IReport';
import { getAutoSaveIntervalInSeconds, getWithUserState } from '../config';
import { getUseHighContrastReportTheme } from '@sas-dvr/internal-va-react-core/config';

const genericError = embeddedBundle['SASReport.genericError.msg']();

function _getCachedReportURLsFromManifest(manifest: ReportManifest) {
  const urls = manifest.getUrls();
  const cachedReportURLVector = [];
  if (urls) {
    for (const url of urls) {
      if (!url) {
        continue;
      }
      const assetType = url.getType();
      if (assetType !== ReportAssetURLType.STP && assetType !== ReportAssetURLType.IMAGE) {
        continue;
      }

      const cachedReportURL = new CachedReportURL();
      cachedReportURL.setName(url.getName());
      cachedReportURL.setType(CachedReportURLType.STP);
      cachedReportURL.setContentKey(url.getContentKey());
      cachedReportURL.setServerFile(url.getServerFile());
      cachedReportURL.setContent(url.getUrlString());

      cachedReportURLVector.push(cachedReportURL);
    }
  }

  return cachedReportURLVector;
}

function _getContentKeyFromManifest(manifest: ReportManifest) {
  //
  // R9 and earlier we simply search through manifest's urls
  //
  const urls = manifest.getUrls();
  if (urls) {
    for (let urlIndex = 0; urlIndex < urls.length; ++urlIndex) {
      const reportAssetURL = urls[urlIndex];
      if (reportAssetURL && reportAssetURL.getType() === ReportAssetURLType.REPORT) {
        return reportAssetURL.getContentKey();
      }
    }
  }

  //
  // R10 and later we check the manifest's files
  //
  const files = manifest.getFiles();
  if (files) {
    for (let fileIndex = 0; fileIndex < files.length; ++fileIndex) {
      const file = files[fileIndex];
      if (file && file.getType() === FileType.REPORT) {
        return file.getContentKey();
      }
    }
  }

  return null;
}

export class SASReport implements IReport {
  @ltjsProperty
  private _report: Report | undefined;

  private _server: SASServer;

  private _identifierOrLocation: string;
  private _reportContent?: string;
  private _reportState?: string;
  private _reportFetched = false;
  private _reloadIgnoreUserState: true | undefined = undefined;
  private _reportGenerationInProgress = false;
  private _reloadPending = false;
  private _errorNotifier: ReplaySubject<string | undefined> = new ReplaySubject(1);
  private _currentError: string | undefined;
  private _reportReadyNotifier = new BehaviorSubject<ReportReadyState>('loading');
  private _destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private _retainCount = 0;
  private _reportSubscribed = false;
  private _userStateSaving = false;

  private _subscribe() {
    if (this._reportSubscribed) {
      return;
    }
    this._reportSubscribed = true;
    this._server
      .getStatusNotifier()
      .pipe(
        takeUntil(this._destroyed$),
        filter((status) => status === 'connected'),
        take(1)
      )
      .subscribe(() => this._fetchReport());
  }

  constructor(server: SASServer, reportUri: string, reportContent?: string, reportState?: string) {
    this._server = server;
    this._identifierOrLocation = reportUri;
    this._reportContent = reportContent;
    this._reportState = reportState;
    this._currentError = server.getError();

    server
      .getErrorNotifier()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((error) => this._setError(error));

    this._subscribe();
  }

  /**
   * Stream for notifying changes to the error state of SASReport.  Re-broadcasts SASServer errors and will replay the
   *   latest error state when subscribed.
   */
  getErrorNotifier(): Observable<string | undefined> {
    return this._errorNotifier;
  }

  /**
   * Stream for monitoring the state of the transport report.
   *
   *  loading - stream initial state or when the report is refreshed
   *  saving - the report is in the process of saving user state.  Saving results in a report refresh
   *  ready - report is ready to be rendered
   */
  getReportStateNotifier(): Observable<ReportReadyState> {
    return this._reportReadyNotifier;
  }

  private _setError(error: string | undefined) {
    if (this._currentError === error) {
      return;
    }
    this._currentError = error;
    this._errorNotifier.next(error);
  }

  private _fetchReport() {
    if (this._reportFetched) {
      throw new Error('Report already fetched.');
    }
    this._reportFetched = true;

    const reportManager = this.getServer().getReportManager();
    if (!reportManager) {
      throw new Error('ReportManager null. Unable to fetch report.');
    }
    const beginGenerate = window.performance.now();

    let rawVoucher;
    if (this._reportContent) {
      rawVoucher = reportManager.getReportByContentForClient(this._reportContent);
    } else if (getWithUserState() && !this._reloadIgnoreUserState) {
      rawVoucher = reportManager.getReportWithUserState(this._identifierOrLocation);
    } else {
      rawVoucher = reportManager.getReport(this._identifierOrLocation);
    }
    this._reloadIgnoreUserState = undefined;
    if (!rawVoucher) {
      throw new Error(genericError);
    }
    this._reportGenerationInProgress = true;
    new TransportVoucher(rawVoucher).always((voucher: TransportVoucher) => {
      this._reportGenerationInProgress = false;
      const endGenerate = window.performance.now();
      console.info('PERFORMANCE: Total time to generate report:', endGenerate - beginGenerate);
      if (this._reloadPending) {
        this._reloadPending = false;
        if (this._deferredUnsubscribe()) {
          return;
        }
        const resource = voucher.getRequestedResource();
        if (resource && resource instanceof Report) {
          this._report = resource;
        }
        this.reload();
        return;
      }
      const warning = voucher.getWarning();
      if (warning) {
        this._setError(warning.getMessage() || genericError);
        this._deferredUnsubscribe();
        return;
      }
      const resource = voucher.getRequestedResource();
      if (!resource || !(resource instanceof Report)) {
        this._setError(
          ResourceManager.getLocalizedString(
            'transport',
            'warning.resource.not.accessible.txt'
          ) as string
        );
        this._deferredUnsubscribe();
        return;
      }
      // Special case: unsubscribe if this report has already been released. See this.release() for details.
      if (!this._deferredUnsubscribe()) {
        this._report = resource;
        this._reportReadyNotifier.next('ready');
      }
    });
  }

  getReportState() {
    return this._reportReadyNotifier.value;
  }

  getError() {
    return this._currentError || this._server.getError();
  }

  private _getMeta() {
    return this._report ? this._report.getMeta() : null;
  }

  get name() {
    const meta = this._getMeta();
    return meta ? meta.getDisplayName() : null;
  }

  get identifierOrLocation() {
    return this._identifierOrLocation;
  }

  get description() {
    const meta = this._getMeta();
    return meta ? meta.getDescription() : null;
  }

  get dateCreated() {
    const meta = this._getMeta();
    return meta ? new Date(meta.getCreatedDatetime()) : null;
  }

  get dateUpdated() {
    const meta = this._getMeta();
    return meta ? new Date(meta.getLastModifiedDatetime()) : null;
  }

  get author() {
    const meta = this._getMeta();
    return meta ? meta.getCreatedBy() : null;
  }

  /**
   * @internal
   */
  createSession(viewSet: ViewSet | null, graphCSS?: string) {
    const manager = this._server.getReportManager();
    if (!manager || !this._report) {
      return null;
    }
    const reportSession = new ReportSession(manager, this._report);
    const configuration = reportSession.getSASReportConfiguration()!;
    // configuration.setFullyQualifiedPathToBIRDReportDirectory(reportItem.getReportPath());
    configuration.setIncludeAllTableColumnsInPickResults(false);
    configuration.setUseSingleThreadMode(false);
    configuration.setUseDefaultGTLDataTips(false);
    configuration.setUseReadOnlyMode(false);
    configuration.setMaximumNumberOfSectionsInMemory(-1);
    configuration.setMetaDataQueryMode(MetaDataQueryMode.LAZY);
    if (getWithUserState()) {
      reportSession.setAutoSavePrimaryState(true);
      const interval = getAutoSaveIntervalInSeconds();
      if (interval) {
        reportSession.setAutoSaveIntervalInSeconds(interval);
      }
    }

    const reportPath = configuration.getFullyQualifiedPathToBIRDReportDirectory();
    if (getUseHighContrastReportTheme()) {
      configuration.setDoVisualStyleOverride(true);
    }
    if (graphCSS !== undefined) {
      let baseCssFilePath;
      {
        // Parse report.xml and find the BaseStyleSheet file
        const reportContent = ltjsModule.getStringContentsOfFile(`${reportPath}/report.xml`);
        if (reportContent) {
          const match = reportContent.match(/<BaseStylesheetResource file="(.*?)"/);
          if (match && match.length === 2) {
            baseCssFilePath = `${reportPath}/${match[1]}`;
          }
        }
      }
      if (baseCssFilePath) {
        // Overwrite the base stylesheet file with the custom css
        ltjsModule.exports.FS.writeFile(baseCssFilePath, graphCSS);
      } else {
        console.error('The base stylesheet was not found so the custom css could not be applied');
      }
    }
    if (this._reportState) {
      ltjsModule.exports.FS.writeFile(`${reportPath}/userStateReport.xml`, this._reportState);
      configuration.setRestoreUserState(true);
    }
    configuration.setComparisonEpsilon(
      this._report.getConnection()!.getServer()!.getMeta()!.getComparisonEpsilon()
    );
    configuration.setTransportURL(this._server.getFinishedURLString());
    const conManager = this._server.getConnectionManager();
    if (!conManager) {
      return null;
    }
    configuration.setMapConnectionInfo(conManager.getMapConnectionInfo());

    const manifest = this._report.getManifest();
    if (manifest) {
      configuration.setCachedReportURLs(_getCachedReportURLsFromManifest(manifest));
      const contentKey = _getContentKeyFromManifest(manifest);
      if (contentKey) {
        configuration.setContentKey(contentKey);
      }
    }

    // null means we are in regular data loading mode.
    // non-null means we are in "viewset" mode.
    configuration.setViewSet(viewSet);
    try {
      reportSession.openReport();
    } catch (e) {
      return null;
    }

    return reportSession;
  }

  reload(ignoreUserState?: true | undefined) {
    this._reportFetched = false;
    this._reloadIgnoreUserState = ignoreUserState;
    if (this._report) {
      this._reportSubscribed = false;
      getDepot().removeReport(this._report);
      this._subscribe();
    } else if (this._reportGenerationInProgress) {
      this._reloadPending = true;
    }
    this._report = undefined;
    this._reportReadyNotifier.next('loading');
  }

  setUserStateSaving(value: boolean) {
    if (this._userStateSaving === value) {
      return;
    }
    this._userStateSaving = value;
    if (value) {
      this._reportReadyNotifier.next('saving');
    } else if (this._retainCount) {
      this.reload();
    }
  }

  getServer() {
    return this._server;
  }

  retain() {
    this._retainCount++;
    return this;
  }

  private _deferredUnsubscribe() {
    if (this._retainCount === 0) {
      this._server.unsubscribe(this.identifierOrLocation);
      return true;
    }
    return false;
  }

  release() {
    if (this._retainCount <= 0) {
      throw new Error('SASReport released without being retained');
    }
    this._retainCount--;

    // Special case: we can get in a bad state if we try to regenerate a report while one is already being generated.
    //  Delay unsubscribe until after report generation to avoid this problem.
    if (this._reportGenerationInProgress) {
      return;
    }

    if (this._retainCount === 0) {
      this._server.unsubscribe(this.identifierOrLocation);
    }
  }

  @ltjsDestructor
  close() {
    if (this._retainCount !== 0) {
      console.error(`SASReport ${this.identifierOrLocation} was destroyed while in use`);
    }
    this._destroyed$.next(true);
  }
}
